import { useUser } from "@clerk/clerk-react";
import { ArrowLeft, Loader, Mail, User } from "lucide-react";
import type React from "react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { z } from "zod";
import { apiClient } from "../lib/apiClient";

// Zod schema for form validation
const addTeamSchema = z.object({
	email: z.string().email("Please enter a valid email address").min(1, "Email is required"),
	fullName: z
		.string()
		.min(1, "Full name is required")
		.max(100, "Full name must be less than 100 characters"),
});

type AddTeamFormData = z.infer<typeof addTeamSchema>;

export default function AddTeam() {
	const { user } = useUser();
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [success, setSuccess] = useState<string | null>(null);
	const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

	const [formData, setFormData] = useState<AddTeamFormData>({
		email: "",
		fullName: "",
	});

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value } = e.target;
		setFormData((prev) => ({
			...prev,
			[name]: value,
		}));

		// Clear validation error for this field when user starts typing
		if (validationErrors[name]) {
			setValidationErrors((prev) => ({
				...prev,
				[name]: "",
			}));
		}
	};

	const validateForm = (): boolean => {
		try {
			addTeamSchema.parse(formData);
			setValidationErrors({});
			return true;
		} catch (err) {
			if (err instanceof z.ZodError) {
				const errors: Record<string, string> = {};
				for (const issue of err.issues) {
					const field = issue.path[0] as string;
					errors[field] = issue.message;
				}
				setValidationErrors(errors);
			}
			return false;
		}
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		if (!user) {
			setError("Please sign in to add team members");
			return;
		}

		// Validate form
		if (!validateForm()) {
			setError("Please fix the validation errors below");
			return;
		}

		setIsLoading(true);
		setError(null);
		setSuccess(null);

		try {
			await apiClient().post("/team/invite", {
				email: formData.email,
				name: formData.fullName,
			});

			setSuccess(`Successfully invited ${formData.fullName} (${formData.email}) to your team!`);

			setFormData({ email: "", fullName: "" });
			// biome-ignore lint/suspicious/noExplicitAny: error shape from axios
		} catch (err: any) {
			console.error("Failed to invite team member:", err);
			setError(err.response?.data?.message || "Failed to send invitation. Please try again.");
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<div className="min-h-screen bg-gradient-to-br from-pink-50 via-orange-50 to-yellow-50">
			<div className="container mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
				{/* Header */}
				<div className="max-w-2xl mx-auto mb-6 sm:mb-8">
					<div className="flex items-center gap-3 sm:gap-4">
						<Link
							to="/team"
							className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors p-2 sm:p-0 -ml-2 sm:ml-0 touch-manipulation"
						>
							<ArrowLeft className="w-5 h-5 sm:w-6 sm:h-6" />
							<span className="hidden sm:inline">Back</span>
						</Link>
						<h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900">
							Add Team Member
						</h1>
					</div>
				</div>

				{/* Main Form */}
				<div className="max-w-2xl mx-auto">
					<div className="bg-white/80 backdrop-blur-sm rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 lg:p-8">
						<form onSubmit={handleSubmit} className="space-y-6">
							{/* Full Name */}
							<div>
								<label
									htmlFor="fullName"
									className="block text-sm sm:text-base font-medium text-gray-700 mb-2"
								>
									Full Name *
								</label>
								<div className="relative">
									<User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
									<input
										type="text"
										id="fullName"
										name="fullName"
										required
										value={formData.fullName}
										onChange={handleInputChange}
										placeholder="John Doe"
										className={`w-full pl-12 pr-4 py-4 sm:py-3 text-base sm:text-sm border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent touch-manipulation ${
											validationErrors.fullName ? "border-red-300 bg-red-50" : "border-gray-300"
										}`}
									/>
								</div>
								{validationErrors.fullName && (
									<p className="mt-1 text-sm text-red-600">{validationErrors.fullName}</p>
								)}
							</div>

							{/* Email */}
							<div>
								<label
									htmlFor="email"
									className="block text-sm sm:text-base font-medium text-gray-700 mb-2"
								>
									Email Address *
								</label>
								<div className="relative">
									<Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
									<input
										data-1p-ignore
										data-lp-ignore={true}
										type="email"
										id="email"
										name="email"
										autoComplete="off"
										autoCapitalize="off"
										required
										value={formData.email}
										onChange={handleInputChange}
										placeholder="john.doe@example.com"
										className={`w-full pl-12 pr-4 py-4 sm:py-3 text-base sm:text-sm border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent touch-manipulation ${
											validationErrors.email ? "border-red-300 bg-red-50" : "border-gray-300"
										}`}
									/>
								</div>
								{validationErrors.email && (
									<p className="mt-1 text-sm text-red-600">{validationErrors.email}</p>
								)}
							</div>

							{/* Error Message */}
							{error && (
								<div className="p-4 bg-red-50 border border-red-200 rounded-lg">
									<p className="text-red-600 text-sm sm:text-base">{error}</p>
								</div>
							)}

							{/* Success Message */}
							{success && (
								<div className="p-4 bg-green-50 border border-green-200 rounded-lg">
									<p className="text-green-600 text-sm sm:text-base">{success}</p>
								</div>
							)}

							{/* Submit Button */}
							<button
								type="submit"
								disabled={isLoading}
								className="w-full bg-gradient-to-r from-orange-400 to-pink-400 hover:from-orange-500 hover:to-pink-500 disabled:from-gray-300 disabled:to-gray-300 text-white font-semibold py-4 sm:py-4 px-6 rounded-lg transition-all duration-300 shadow-lg flex items-center justify-center gap-2 text-base sm:text-lg touch-manipulation min-h-[56px]"
							>
								{isLoading ? (
									<>
										<Loader className="w-5 h-5 animate-spin" />
										<span>Adding Team Member...</span>
									</>
								) : (
									"Add Team Member"
								)}
							</button>
						</form>
					</div>
				</div>
			</div>
		</div>
	);
}
